// utils/skillSpeed.js
// Centralized speed calculation for all skills
// Eliminates duplicate getEffectiveSpeedMultiplier() implementations

import { applyMasteryToMultiplier } from '../systems/experience.js';
import {
    getTalentSpeedBonus,
    getTalentToolBonus,
    getStackingSpeedBonus
} from '../systems/talentTree/talentTree.js';
import { IS_DEV } from '../config/env.js';
import { adminPanel } from '../skills/admin/admin.js';

/**
 * Calculate the effective speed multiplier for a skill
 * Includes: tool speed, talent tool bonus, mastery, admin override, talent speed, stacking bonus
 *
 * @param {string} skillId - The skill ID (from SKILLS constant)
 * @param {Object} activeTool - The tool object with speedMultiplier property
 * @param {Object} options - Optional overrides
 * @param {number} options.stackingCap - Override stacking bonus cap (e.g., 0.25 for extended rhythm)
 * @returns {number} The effective speed multiplier
 */
export function calculateEffectiveSpeed(skillId, activeTool, options = {}) {
    let baseMultiplier = activeTool?.speedMultiplier || 1;

    // Apply talent tool bonus (increases tool effectiveness)
    const toolBonus = getTalentToolBonus(skillId);
    if (toolBonus > 0) {
        baseMultiplier *= (1 + toolBonus);
    }

    // Apply mastery bonus (doubles tool effect at level 120)
    let multiplier = applyMasteryToMultiplier(skillId, baseMultiplier);

    // Apply admin override if in dev mode
    if (IS_DEV && activeTool?.id) {
        multiplier = adminPanel.getEffectiveToolSpeed(activeTool.id, multiplier);
    }

    // Apply talent speed bonus (with optional subType filter for skills like alchemy)
    const speedBonus = getTalentSpeedBonus(skillId, options.subType || null);
    if (speedBonus > 0) {
        multiplier *= (1 + speedBonus);
    }

    // Apply stacking speed bonus (various rhythm/flow talents)
    let stackingBonus = getStackingSpeedBonus(skillId);
    // Allow override of stacking cap (e.g., Extended Rhythm increases from 15% to 25%)
    if (options.stackingCap !== undefined && stackingBonus > 0) {
        stackingBonus = Math.min(stackingBonus, options.stackingCap);
    }
    if (stackingBonus > 0) {
        multiplier *= (1 + stackingBonus);
    }

    return multiplier;
}

/**
 * Get tool management helpers for a skill
 * Reduces boilerplate in skill classes
 *
 * @param {string} skillId - The skill ID
 * @param {Object} toolsObject - The TOOLS/RODS/PICKAXES constant object
 * @param {string} defaultToolKey - Key of the default tool (e.g., 'BASIC', 'BRONZE')
 * @param {Object} gameState - The gameState instance (passed to avoid circular import)
 * @returns {Object} Helper functions: initializeStartingTool, getActiveBuff, getCurrentTool
 */
export function createToolHelpers(skillId, toolsObject, defaultToolKey, gameState) {
    const defaultTool = toolsObject[defaultToolKey];
    const toolsArray = Object.values(toolsObject);

    return {
        /**
         * Initialize the starting tool - ensure it's owned and equipped
         */
        initializeStartingTool() {
            if (!gameState.hasPurchasedTool(skillId, defaultTool.id)) {
                gameState.purchaseTool(skillId, defaultTool.id, 0);
            }
            if (!gameState.getEquippedTool(skillId)) {
                gameState.equipTool(skillId, defaultTool.id);
            }
        },

        /**
         * Get the highest tier owned tool (determines actual speed buff)
         */
        getActiveBuff() {
            return gameState.getHighestPurchasedTool(skillId, toolsArray) || defaultTool;
        },

        /**
         * Get the currently equipped tool object
         */
        getCurrentTool() {
            const equippedId = gameState.getEquippedTool(skillId);
            return toolsArray.find(t => t.id === equippedId) || defaultTool;
        }
    };
}

export default {
    calculateEffectiveSpeed,
    createToolHelpers
};
